import type { ArchiveReceipt, IntakeView } from "./partIntakes";

/** A successful HTTP response can still be preparation or verification in progress.
 * Preserve this identity for GET/reverification; callers must never retry creation. */
export class PartArchivePendingError extends Error {
  readonly name = "PartArchivePendingError";
  readonly retryOnly = true;
  readonly intakeId: string | undefined;
  readonly receipt: ArchiveReceipt | undefined;
  constructor(readonly payload: IntakeView | ArchiveReceipt | unknown) {
    super("建档尚未核验完成。请继续查看已有建档状态，不要重复创建零件。");
    const body =
      payload && typeof payload === "object"
        ? (payload as Partial<IntakeView & ArchiveReceipt>)
        : {};
    this.intakeId = body.intake_id ?? body.receipt?.intake_id;
    this.receipt =
      body.receipt ??
      (body.archive_status ? (body as ArchiveReceipt) : undefined);
  }
}
export type PartWriteResponse<T> = T | IntakeView | ArchiveReceipt;
export function requireVerifiedPart<T extends { id: string }>(response: {
  status: number;
  data: PartWriteResponse<T>;
}): T {
  if (
    response.status === 202 ||
    !("id" in response.data) ||
    typeof response.data.id !== "string" ||
    !response.data.id
  ) {
    throw new PartArchivePendingError(response.data);
  }
  return response.data as T;
}

export function requireVerifiedSourceWrite<
  T extends { receipt?: ArchiveReceipt },
>(response: { status: number; data: T }): T {
  const receipt = response.data.receipt;
  if (
    response.status === 202 ||
    !receipt ||
    receipt.archive_status !== "archived" ||
    !receipt.verified_at ||
    !receipt.verification_observed_at
  ) {
    throw new PartArchivePendingError(response.data);
  }
  return response.data;
}

export function isVerifiedArchive(
  receipt?: ArchiveReceipt | null,
): receipt is ArchiveReceipt & {
  part: { id: string; code: string };
  part_revision: { id: string };
} {
  return !!(
    receipt?.archive_status === "archived" &&
    receipt.verified_at &&
    receipt.verification_observed_at &&
    receipt.part?.id &&
    receipt.part_revision?.id
  );
}
